import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArtworkImageGallery } from "../components/ArtworkImageGallery";
import { ArtworkShareMenu } from "../components/ArtworkShareMenu";
import { ShippingCalculator } from "../components/ShippingCalculator";
import RoomViewer from "../components/RoomViewer";
import { logger } from "../lib/logger";
import Seo, { productLd, breadcrumbLd } from "../components/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [shipQuote, setShipQuote] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/artworks/${id}`);
        setArt(data);
      } catch (err) {
        logger.error("Artwork detail load failed:", err);
        toast.error("Artwork not found");
        navigate("/gallery");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleBuy = async () => {
    if (!user) {
      toast.error("Please sign in to purchase");
      navigate("/login");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        artwork_id: art.id,
        origin_url: window.location.origin,
        shipping_country: shipQuote?.country || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setBuying(false);
    }
  };

  if (loading || !art) {
    return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;
  }

  return (
    <div data-testid="artwork-detail-page" className="px-6 md:px-12 lg:px-24 py-16">
      <Seo
        title={`${art.title} — ${art.medium || "Original Art"} by ${art.artist_name || "African master"}`}
        description={
          art.description
            ? `${art.medium || "Original artwork"} by ${art.artist_name}. ${art.description.slice(0, 110)}${art.description.length > 110 ? "…" : ""}`
            : `${art.title} — ${art.medium || "original artwork"} by ${art.artist_name || "an African master"}. Authentic African art for sale at AfricaArtMasters.${art.country ? ` From ${art.country}.` : ""}`
        }
        type="product"
        image={`${BACKEND_URL}/api/flyer/artwork/${art.id}/image?fmt=jpg&size=landscape`}
        imageAlt={`${art.title} by ${art.artist_name}`}
        path={`/artwork/${art.id}`}
        jsonLd={[
          productLd({
            artwork: art,
            url: `${window.location.origin}/artwork/${art.id}`,
            image: `${BACKEND_URL}/api/flyer/artwork/${art.id}/image?fmt=jpg&size=landscape`,
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Gallery", path: "/gallery" },
            { name: art.title, path: `/artwork/${art.id}` },
          ]),
        ]}
      />
      <Link to="/gallery" className="label-eyebrow text-[#555] hover:text-[#1E3A8A]">
        ← Back to Gallery
      </Link>
      <div className="mt-10 grid grid-cols-12 gap-y-10 gap-x-4 lg:gap-10">
        <div className="col-span-12 lg:col-span-8">
          <ArtworkImageGallery
            images={[art.image_url, ...(art.gallery || [])]}
            title={art.title}
            artist={art.artist_name}
            medium={art.medium}
          />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <p className="label-eyebrow text-[#555]">{art.medium} · {art.country}</p>
          <h1 data-testid="artwork-title" className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
            {art.title}
          </h1>
          <Link
            to={`/artist/${art.artist_id}`}
            className="block mt-4 font-serif-display text-2xl italic text-[#1E3A8A] hover:underline"
            data-testid="artwork-artist-link"
          >
            {art.artist_name}
          </Link>
          {art.art_center_id && (
            <Link
              to={`/art-hub/${art.art_center_id}`}
              data-testid="artwork-center-link"
              className="block mt-2 label-eyebrow text-[#555] hover:text-[#1E3A8A]"
            >
              Presented by {art.art_center_name}
            </Link>
          )}

          <div className="mt-10 space-y-4">
            <div className="flex justify-between border-b border-[#E5E0D8] pb-3">
              <span className="label-eyebrow text-[#555]">Year</span>
              <span className="font-mono-aam text-sm">{art.year || "—"}</span>
            </div>
            <div className="flex justify-between border-b border-[#E5E0D8] pb-3">
              <span className="label-eyebrow text-[#555]">Dimensions</span>
              <span className="font-mono-aam text-sm">{art.dimensions || "—"}</span>
            </div>
            <div className="flex justify-between border-b border-[#E5E0D8] pb-3">
              <span className="label-eyebrow text-[#555]">Price</span>
              <span className="font-mono-aam text-base" data-testid="artwork-price">
                ${Number(art.price).toLocaleString()} {art.currency}
              </span>
            </div>
            {shipQuote && (
              <div className="flex justify-between border-b border-[#E5E0D8] pb-3" data-testid="artwork-total-line">
                <span className="label-eyebrow text-[#555]">Total · incl. shipping</span>
                <span className="font-mono-aam text-base text-[#1E3A8A]">
                  ${(Number(art.price) + Number(shipQuote.price || 0)).toLocaleString()} {art.currency}
                </span>
              </div>
            )}
          </div>

          <p className="text-[#333] mt-8 leading-relaxed font-light">{art.description}</p>

          <ShippingCalculator artworkId={art.id} onChange={setShipQuote} />

          <Button
            onClick={handleBuy}
            disabled={art.sold || buying}
            data-testid="buy-artwork-button"
            className="mt-10 w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-7 h-auto label-eyebrow disabled:opacity-50"
          >
            {art.sold ? "Sold" : buying ? "Redirecting…" : "Acquire this work"}
          </Button>
          <p className="label-eyebrow text-[#555] mt-4 text-center">
            Secure checkout · Direct from artist
          </p>
          <div className="mt-4">
            <ArtworkShareMenu artwork={art} />
          </div>
        </div>
      </div>

      {/* "See it in your space" (v1.28) — framed-in-a-room mockups so buyers
          can visualise the work in their home before committing. */}
      {art.image_url && (
        <RoomViewer
          artworkId={art.id}
          artworkUrl={art.image_url.startsWith("/api") ? `${BACKEND_URL}${art.image_url}` : art.image_url}
          artworkTitle={art.title}
          medium={art.medium}
          dimensions={art.dimensions}
          gallery={Array.isArray(art.gallery)
            ? art.gallery.map((u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u))
            : null}
        />
      )}
    </div>
  );
}
